import React, { createContext, useContext, useState } from 'react';
import './PopUpContext.css';

export interface PopupContextType {
  showPopup: (
    content: React.ReactNode | ((close: () => void) => React.ReactNode),
    onClose?: () => void
  ) => void;
  closePopup: () => void;
}

const PopupContext = createContext<PopupContextType | null>(null);

export const PopupProvider = ({ children }: { children: React.ReactNode }) => {
  const [popupConfig, setPopupConfig] = useState<{
    isOpen: boolean;
    content: React.ReactNode | ((close: () => void) => React.ReactNode);
    onClose: () => void;
  }>({
    isOpen: false,
    content: null,
    onClose: () => setPopupConfig(p => ({ ...p, isOpen: false })),
  });

  const closePopup = () => {
    if (popupConfig.onClose) popupConfig.onClose();
    else setPopupConfig(p => ({ ...p, isOpen: false }));
  };
  const showPopup = (
    content: React.ReactNode | ((close: () => void) => React.ReactNode),
    onClose?: () => void
  ) => {
    setPopupConfig({
      isOpen: true,
      content,
      onClose: onClose || (() => setPopupConfig(p => ({ ...p, isOpen: false }))),
    });
  };

  return (
    <PopupContext.Provider value={{ showPopup, closePopup }}>
      {children}
      {popupConfig.isOpen && (
        <div className="studio-popup-backdrop">
          <div className="studio-popup-container">
            {typeof popupConfig.content === 'function'
              ? popupConfig.content(closePopup)
              : popupConfig.content}
          </div>
        </div>
      )}
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};
